import type { LedgerRow } from "@m8t-stack/agent-ledger/read";
import type { DreamCursor, LedgerSource } from "./types.js";
import { SOURCES, WORKER_ALIASES, canonicalWorker } from "./types.js";

/** Reverse the canonical→alias map: given a worker, return every physical
 *  PartitionKey to query — the worker itself plus any frozen alias that
 *  canonicalizes to it (e.g. `azzy` → ["azzy", "azure-advisor"]).
 *  Canonicalizes the request first so a request for a frozen alias fans out
 *  identically. Pure. */
export function physicalPksFor(worker: string): string[] {
  const canonical = canonicalWorker(worker);
  const pks = new Set<string>([canonical]);
  for (const [physical, mapped] of Object.entries(WORKER_ALIASES)) {
    if (mapped === canonical) pks.add(physical);
  }
  return [...pks];
}

/** Collapse a ledger `source` to the closed set, else "unknown"
 *  (open union, defensive; never guessed, never dropped). Pure. */
export function sourceBucket(source: string): string {
  return (SOURCES as readonly string[]).includes(source) ? source : "unknown";
}

/**
 * The injected ledger-read seam. The entry point binds this to a live
 * `TableClient` + `fetchLedgerRows` from `@m8t-stack/agent-ledger/read`; unit
 * tests bind an in-memory fake. `sinceTs` is the inclusive `eventTimestamp ge`
 * floor for ONE physical PK ("" = from the beginning). The concrete binding
 * MUST rely on `listEntities` auto-pagination and pass NO `$top` (a naive $top
 * reintroduces the probe's invalid-input-at-2000 bug).
 */
export type LedgerReadFn = (pk: string, sinceTs: string) => Promise<LedgerRow[]>;

const MS_PER_SECOND = 1000;

/** watermark.ts − safetyLag, ISO-8601 UTC. "" (from-the-beginning) when the
 *  PK has no stored watermark. Pure: derives only from the cursor, never the
 *  wall clock (the overlap is anchored to the watermark, not `now`). */
function overlapFloor(ts: string | undefined, safetyLagSeconds: number): string {
  if (!ts) return "";
  const parsed = Date.parse(ts);
  if (!Number.isFinite(parsed)) return "";
  return new Date(parsed - safetyLagSeconds * MS_PER_SECOND).toISOString();
}

/**
 * `LedgerSource` over an injected per-PK read fn. Per physical PK: query from
 * the trailing-overlap floor (watermark.ts − safetyLag), then drop rows whose
 * RowKey is already in that PK's `consumedRowKeys`. Dedup is per-PK (azzy and
 * azure-advisor are independent partitions) and keyed on the storage
 * RowKey — the only sound unique key (a2a eventIds repeat).
 */
export function makeLedgerSource(read: LedgerReadFn): LedgerSource {
  return {
    async rowsSince(physicalPks: string[], cursor: DreamCursor): Promise<LedgerRow[]> {
      const out: LedgerRow[] = [];
      for (const pk of physicalPks) {
        const wm = Object.hasOwn(cursor.watermarks, pk) ? cursor.watermarks[pk] : undefined;
        const sinceTs = overlapFloor(wm?.ts, cursor.safetyLagSeconds);
        const consumed = new Set(wm?.consumedRowKeys ?? []);
        const rows = await read(pk, sinceTs);
        for (const r of rows) {
          if (consumed.has(r.rowKey)) continue;
          out.push(r);
        }
      }
      return out;
    },
  };
}
